import { useSearch } from "@tanstack/react-router";
import { Button, Col, Input, Row, Select } from "antd";
import Title from "antd/es/typography/Title";
import { IncomingPostbackTestDrawer } from "components/Drawers/IncomingPostbackTestDrawer/IncomingPostbackTestDrawer.tsx";
import { DrawerLink } from "components/UI/DrawerLink";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { ConversionTypeSelectSearchable } from "components/UI/Selects/ConversionTypeSelectSearchable/ConversionTypeSelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { OfferSelectSearchable } from "components/UI/Selects/OfferSelectSearchable/OfferSelectSearchable.tsx";
import { PartnerSelectSearchable } from "components/UI/Selects/PartnerSelectSearchable/PartnerSelectSearchable.tsx";
import { SourceSelectSearchable } from "components/UI/Selects/SourceSelectSearchable/SourceSelectSearchable.tsx";
import { StatusSelectSearchable } from "components/UI/Selects/StatusSelectSearchable/StatusSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { MULTISELECT_ALL_VALUE } from "localConstants";
import { useEffect, useRef } from "react";
import { FormItem } from "react-hook-form-antd";
import { DrawerId } from "types/models";

import IncomingPostbackDataOptional = App.Data.IncomingPostbackDataOptional;

interface IncomingPostbackGeneralFormSectionProps {
    incomingPostback?: IncomingPostbackDataOptional;
}
interface SearchParams {
    [`incomingPostbackTest-id`]: string;
}

const hasSameValues = <T,>(current: T[], next: T[]) =>
    current.length === next.length && current.every((value, index) => value === next[index]);

export const IncomingPostbackGeneralFormSection = ({
    incomingPostback,
}: IncomingPostbackGeneralFormSectionProps = {}) => {
    const { t } = usePaldockTranslation("forms.incoming-postback-general");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");
    const { control, fieldNames, watch, setValue } = useGeneralForm();
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const includedOffers = watch("offer_ids") ?? [];
    const excludedOffers = watch("excluded_offer_ids") ?? [];
    const includedIsAll = includedOffers.includes(MULTISELECT_ALL_VALUE);
    const excludedIsAll = excludedOffers.includes(MULTISELECT_ALL_VALUE);
    const includedHasSpecific = includedOffers.length > 0 && !includedIsAll;
    const excludedHasSpecific = excludedOffers.length > 0 && !excludedIsAll;
    const previousIncludedIsAll = useRef(includedIsAll);
    const previousExcludedIsAll = useRef(excludedIsAll);

    useEffect(() => {
        const wasIncludedAll = previousIncludedIsAll.current;
        const wasExcludedAll = previousExcludedIsAll.current;

        if (!includedOffers.length && !excludedOffers.length) {
            const nextIncludedOffers =
                wasExcludedAll && !wasIncludedAll ? [MULTISELECT_ALL_VALUE] : ([] as typeof includedOffers);
            const nextExcludedOffers =
                wasIncludedAll && !wasExcludedAll ? [MULTISELECT_ALL_VALUE] : ([] as typeof excludedOffers);

            if (!hasSameValues(includedOffers, nextIncludedOffers)) {
                setValue("offer_ids", nextIncludedOffers);
            }

            if (!hasSameValues(excludedOffers, nextExcludedOffers)) {
                setValue("excluded_offer_ids", nextExcludedOffers);
            }
        } else if (includedIsAll && excludedIsAll) {
            if (!wasIncludedAll && wasExcludedAll && excludedOffers.length > 0) {
                setValue("excluded_offer_ids", []);
            } else if (!wasExcludedAll && wasIncludedAll && includedOffers.length > 0) {
                setValue("offer_ids", []);
            }
        }

        previousIncludedIsAll.current = includedIsAll;
        previousExcludedIsAll.current = excludedIsAll;
    }, [excludedIsAll, excludedOffers, includedIsAll, includedOffers, setValue]);

    return (
        <>
            <div className="flex flex-col gap-y-6">
                <SectionContentWrapper withBackground>
                    <Title level={3} className="!mb-6">
                        {tC("settings")}
                    </Title>
                    <Row gutter={16}>
                        <Col span={6}>
                            <FormItem
                                control={control}
                                name={fieldNames.name?.name || "name"}
                                label={tFields("name.label")}
                            >
                                <Input placeholder={tFields("name.placeholder")} />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem
                                control={control}
                                name={fieldNames.is_active?.name || "is_active"}
                                label={tFields("status.label")}
                            >
                                <StatusSelectSearchable />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem
                                control={control}
                                name={fieldNames.sources?.name || "sources"}
                                label={tFields("sources.label")}
                            >
                                <SourceSelectSearchable mode="multiple" allowClear />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem
                                control={control}
                                name={fieldNames.country_id?.name || "country_id"}
                                label={tFields("country.label")}
                            >
                                <CountrySelectSearchable allowClear />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <FormItem
                                control={control}
                                name={fieldNames.tags?.name || "tags"}
                                label={t("fields.tags.label")}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    disabled
                                    placeholder={t("fields.tags.placeholder")}
                                />
                            </FormItem>
                        </Col>
                    </Row>

                    {incomingPostback && (
                        <Row gutter={8} className="mt-3">
                            <Col span={6}>
                                <div className={"flex items-center gap-x-3"}>
                                    <Button type="primary" size={"middle"}>
                                        {t("buttons.log")}
                                    </Button>
                                    <DrawerLink
                                        drawerId={DrawerId.IncomingPostbackTest}
                                        search={{
                                            [`${DrawerId.IncomingPostbackTest}-id`]: incomingPostback.id ?? "",
                                        }}
                                        action="show"
                                    >
                                        <Button type="primary" size={"middle"}>
                                            {t("buttons.run")}
                                        </Button>
                                    </DrawerLink>
                                </div>
                            </Col>
                        </Row>
                    )}
                </SectionContentWrapper>

                <SectionContentWrapper withBackground>
                    <Title level={3} className="!mb-6">
                        {t("sections.conditions")}
                    </Title>
                    <Row gutter={16}>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.types?.name || "types"}
                                label={tFields("type.label")}
                            >
                                <ConversionTypeSelectSearchable mode="multiple" allowClear />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.affiliate_ids?.name || "affiliate_ids"}
                                label={tFields("affiliates.label")}
                            >
                                <PartnerSelectSearchable mode="multiple" withAllOption />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.advertiser_ids?.name || "advertiser_ids"}
                                label={tFields("advertisers.label")}
                            >
                                <AdvertiserSelectSearchable mode="multiple" withAllOption />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <FormItem
                                control={control}
                                name={fieldNames.offer_ids?.name || "offer_ids"}
                                label={tFields("included_offers.label")}
                            >
                                <OfferSelectSearchable mode="multiple" withAllOption disabled={excludedHasSpecific} />
                            </FormItem>
                        </Col>
                        <Col span={12}>
                            <FormItem
                                control={control}
                                name={fieldNames.excluded_offer_ids?.name || "excluded_offer_ids"}
                                label={tFields("excluded_offers.label")}
                            >
                                <OfferSelectSearchable mode="multiple" withAllOption disabled={includedHasSpecific} />
                            </FormItem>
                        </Col>
                    </Row>
                </SectionContentWrapper>
            </div>
            {searchParams[`${DrawerId.IncomingPostbackTest}-id`] && <IncomingPostbackTestDrawer />}
        </>
    );
};
